import { ChevronRight, Close } from '@mui/icons-material';
import { Box, Card, CardHeader, Divider, FormControlLabel, IconButton, Popover, Radio, RadioGroup, Typography } from '@mui/material';
import strings from '@src/app/strings/LocalisedStrings';
import Colors from '@src/app/config/colorConfig';
import { percentageFormatter } from '@src/app/utils/GenericUtils';
import { BenchmarkInfo, MultiPortfolioPerformanceDetails } from 'clientportalshared';
import { Dispatch, FC, SetStateAction, useState } from 'react';

export interface BenchmarkCompareCardProps {
  benchmarks: BenchmarkInfo[];
  benchmarkSelected: BenchmarkInfo | null;
  setBenchmarkSelected: Dispatch<SetStateAction<BenchmarkInfo | null>>;
  data: MultiPortfolioPerformanceDetails;
}

export const BenchmarkCompareCard: FC<BenchmarkCompareCardProps> = ({
  benchmarks,
  setBenchmarkSelected,
  benchmarkSelected,
  data,
}: BenchmarkCompareCardProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  const dailyBenchmark = data?.dailyBenchmark ?? [];
  const endValue = dailyBenchmark[dailyBenchmark.length - 1];

  const handleIndexSelection = (event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    const benchmark = benchmarks.find((b) => b.code === value);
    if (benchmark) {
      setBenchmarkSelected(benchmark);
    } else {
      setBenchmarkSelected(null);
    }
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event: React.MouseEvent<HTMLDivElement> | React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(null);
  };

  return (
    <Card
      elevation={0}
      onClick={handleClick}
      sx={{
        borderRadius: '13.85px',
        border: `1px solid ${Colors.gray300}`,
        boxShadow: '0px 1.84px 10.11px 1.84px #E4E4E4',
        ':hover': {
          cursor: 'pointer',
        },
        minHeight: '100%',
      }}
    >
      <CardHeader
        title={benchmarkSelected ? `${benchmarkSelected.name}` : 'Compare Against Benchmark'}
        subheader={benchmarkSelected && endValue ? percentageFormatter(endValue.cumulativeReturn ?? 0, 2) : ''}
        titleTypographyProps={{
          fontWeight: 700,
          textTransform: 'uppercase',
          fontSize: 18,
        }}
        subheaderTypographyProps={{
          fontWeight: 700,
          fontSize: 20,
        }}
        action={<ChevronRight sx={{ color: '#D7D7D7' }} />}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{ borderRadius: '13.85px' }}
      >
        <Box display="flex" flexDirection="column" padding={2} gap={1}>
          <Box display="flex" justifyContent="space-between" gap={2}>
            <Typography variant="subtitle2" sx={{ textTransform: 'uppercase' }}>
              {strings.chooseBenchmark}
            </Typography>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Box>
          <Typography>{strings.chooseBenchmarkDescription}</Typography>

          <Divider />
          <RadioGroup onChange={handleIndexSelection} value={benchmarkSelected?.code || 'none'}>
            <div>
              <FormControlLabel value="none" control={<Radio />} label={strings.none} />
              <Divider />
            </div>
            {benchmarks
              .filter((benchmark) => !benchmark.code?.startsWith(strings.noBenchmarkApplicable))
              .map((benchmarkDatum, index) => (
                <div key={benchmarkDatum.benchmarkKey}>
                  <FormControlLabel value={benchmarkDatum.code} control={<Radio />} label={benchmarkDatum.name!} />
                  {index < benchmarks.length - 1 && <Divider />}
                </div>
              ))}
          </RadioGroup>
        </Box>
      </Popover>
    </Card>
  );
};
